import React, { useState } from 'react';
import { FileText, ArrowsClockwise, Spinner, DownloadSimple, Trash, Quotes, CaretDown, CaretRight, LinkSimple } from '@phosphor-icons/react';
import { API } from '@/lib/api';

const GenDocItem = ({ caseId, d, deleteGenDoc }) => {
  const [showSources, setShowSources] = useState(false);
  const sources = d.sources || { documents: [], laws: [] };
  const srcCount = (sources.documents?.length || 0) + (sources.laws?.length || 0);

  return (
    <div className="p-3 bg-white border border-[#E5E7EB] rounded-md" data-testid={`gen-doc-${d._id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="font-ibm font-semibold text-sm text-[#09090B] truncate" title={d.title}>{d.title}</p>
          <div className="text-[11px] text-[#6B7280] font-ibm mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#09090B] font-semibold uppercase tracking-wider text-[10px]">{(d.doc_type || 'document').replace(/_/g, ' ')}</span>
            <span>{new Date(d.created_at).toLocaleString()}</span>
            <span>· {Math.round((d.docx_size || 0) / 1024)} KB docx · {Math.round((d.pdf_size || 0) / 1024)} KB pdf</span>
            {srcCount > 0 && (
              <button onClick={() => setShowSources(s => !s)} data-testid={`gen-doc-sources-toggle-${d._id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] font-semibold text-[10px] hover:bg-[#002FA7]/20 transition-all">
                <Quotes size={10} weight="fill" /> {srcCount} source{srcCount !== 1 ? 's' : ''} {showSources ? <CaretDown size={9} /> : <CaretRight size={9} />}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <a href={`${API}/cases/${caseId}/generated-documents/${d._id}/download?fmt=docx`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-ibm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00237D] transition-all"
            data-testid={`gen-doc-download-docx-${d._id}`}>
            <DownloadSimple size={12} /> .docx
          </a>
          <a href={`${API}/cases/${caseId}/generated-documents/${d._id}/download?fmt=pdf`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-ibm font-semibold bg-[#FF9500] text-white rounded-md hover:bg-[#E08600] transition-all"
            data-testid={`gen-doc-download-pdf-${d._id}`}>
            <DownloadSimple size={12} /> .pdf
          </a>
          <button onClick={() => deleteGenDoc(d._id)} title="Delete"
            className="inline-flex items-center justify-center w-7 h-7 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
            data-testid={`gen-doc-delete-${d._id}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>
      {showSources && srcCount > 0 && (
        <div className="mt-2 pt-2 border-t border-[#F3F4F6]" data-testid={`gen-doc-sources-${d._id}`}>
          {sources.documents?.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mb-1">Documents Cited</p>
              <ul className="space-y-0.5">
                {sources.documents.map(s => (
                  <li key={`sd-${s.ref}`} className="text-xs font-ibm text-[#09090B]">
                    <span className="font-semibold text-[#002FA7]">[{s.ref}]</span> {s.file_name}
                    {s.note && <span className="text-[#6B7280]"> — {s.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sources.laws?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mb-1">Legal Authorities</p>
              <ul className="space-y-0.5">
                {sources.laws.map((law, i) => (
                  <li key={`sl-${i}`} className="text-xs font-ibm text-[#09090B]">
                    {law.title}{law.citation && <span className="text-[#6B7280]"> — {law.citation}</span>}
                    {law.official_url && (
                      <a href={law.official_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 ml-1 text-[#002FA7] hover:underline">
                        <LinkSimple size={10} /> source
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const GeneratedDocumentsList = ({ caseId, genDocs, genDocsLoading, loadGenDocs, deleteGenDoc }) => (
  <div className="mt-5 pt-5 border-t border-[#E5E7EB]" data-testid="generated-documents-section">
    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <h4 className="font-outfit text-base font-semibold text-[#09090B] flex items-center gap-2">
        <FileText size={18} className="text-[#002FA7]" weight="fill" /> Generated Documents
        {genDocs.length > 0 && (
          <span className="text-xs font-ibm text-[#6B7280] font-normal">· {genDocs.length}</span>
        )}
      </h4>
      <button onClick={loadGenDocs} disabled={genDocsLoading} className="text-xs font-ibm text-[#6B7280] hover:text-[#002FA7] flex items-center gap-1">
        {genDocsLoading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />} Refresh
      </button>
    </div>
    {genDocs.length === 0 ? (
      <p className="text-xs text-[#6B7280] font-ibm italic">Ask the AI to draft a report, chronology, letter, or any other document — it will appear here, downloadable as .docx and .pdf with inline citations and a verified Sources appendix.</p>
    ) : (
      <div className="space-y-2">
        {genDocs.map(d => (
          <GenDocItem key={d._id} caseId={caseId} d={d} deleteGenDoc={deleteGenDoc} />
        ))}
        <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 font-ibm">
          ⚠ You are responsible for the content of any generated document. Review carefully before sending or filing — this is AI-assisted output, not legal advice.
        </p>
      </div>
    )}
  </div>
);
